//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// team-settings-admin-no-meeting-day imports
import { IContainer, IProps, IState } from "./TeamSettingsAdminNoMeetingDay.d";
import {
  teamSettingsAdminNoMeetingDayFragments,
  refetchQuery,
} from "./TeamSettingsAdminNoMeetingDay.gql";
// other imports
import { H2, A } from "@ui";
import { Switch } from "@clockwise/design-system";
// util
import { maybeGetTeamSettingsFromOrg } from "@util/team-settings.util";
import { logger } from "@util/logger.util";
import { track, TrackingEvents } from "@util/analytics.util";
import classNames from "classnames";
// constants
import { articleUrls } from "@clockwise/client-commons/src/constants/help-center";
import { daysOfWeek } from "@constants/date.constant";
// state
import { snackBarOpen } from "@actions/snack-bar.actions";
import { saveTeamSettings } from "@mutations";
// material-ui
import { Checkbox, FormControl, FormControlLabel, MenuItem, Select } from "@material-ui/core";

//////////////////
// COMPONENT
//////////////////
class TeamSettingsAdminNoMeetingDayCmpt extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    const teamSettings = maybeGetTeamSettingsFromOrg(props.org);

    this.state = {
      noMeetingDayEnabled: teamSettings ? teamSettings.useNoMeetingDay : false,
      noMeetingDayOfWeek: teamSettings
        ? teamSettings.noMeetingDayOfWeek
        : ISchema.NoMeetingDayOfWeek.Wednesday,
      autoreplyEnabled: teamSettings ? teamSettings.noMeetingDayEmailOnScheduleOver : false,
    };
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private updateTeamSettings = () => {
    const { relay, org, team } = this.props;
    const { noMeetingDayEnabled, noMeetingDayOfWeek, autoreplyEnabled } = this.state;

    const input: ISchema.ISaveTeamSettingsMutationInput = {
      orgRelayId: org.id,
      teamId: team.teamId,
      useNoMeetingDay: noMeetingDayEnabled,
      noMeetingDayOfWeek: noMeetingDayOfWeek,
      noMeetingDayEmailOnScheduleOver: autoreplyEnabled,
    };

    saveTeamSettings(
      relay.environment,
      input,
      () => {
        this.props.snackBarOpen(`Updated your team's no-meeting day preferences`);
      },
      () => {
        const msg = `Failed to update your team's no-meeting day preferences`;
        this.props.snackBarOpen(msg);
        logger.error(msg);
      },
    );
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  private handleNoMeetingDayEnabledDayToggle = () => {
    const { noMeetingDayEnabled } = this.state;
    const newSyncValue = !noMeetingDayEnabled;

    this.setState({ noMeetingDayEnabled: newSyncValue, autoreplyEnabled: newSyncValue }, () => {
      this.updateTeamSettings();
    });

    track(TrackingEvents.TEAMS.TEAMS_NO_MEETING_DAY_TOGGLED, { noMeetingDayEnabled: newSyncValue });
  };

  private onDayOfWeekChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ noMeetingDayOfWeek: event.target.value as ISchema.NoMeetingDayOfWeek }, () => {
      this.updateTeamSettings();
    });

    track(TrackingEvents.TEAMS.TEAMS_NO_MEETING_DAY_OF_WEEK_SELECTED, {
      dayOfWeek: event.target.value,
    });
  };

  private handleAutoreplyEnabledDayToggle = () => {
    const { autoreplyEnabled } = this.state;
    const newSyncValue = !autoreplyEnabled;

    this.setState({ autoreplyEnabled: newSyncValue }, () => {
      this.updateTeamSettings();
    });

    track(TrackingEvents.TEAMS.TEAMS_NO_MEETING_DAY_AUTOREPLY_TOGGLED, {
      autoreplyEnabled: newSyncValue,
    });
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  public render() {
    const { isWebSettings } = this.props;
    const { noMeetingDayEnabled, autoreplyEnabled, noMeetingDayOfWeek } = this.state;

    return (
      <React.Fragment>
        <div className="cw-mt-8">
          {!isWebSettings && (
            <>
              <H2 style={{ fontSize: 18 }}>Team no-meeting day</H2>
              <div className="cw-body-base cw-text-slate-standard cw-mb-4">
                Your team can dedicate one day of the week for a “no-meeting day.” Clockwise will
                try to keep that day meeting-free for team members, and can optionally notify
                meeting organizers when they schedule over your team’s no-meeting day.{" "}
                <A href={articleUrls.noMeetingDay} target="_blank">
                  Learn more
                </A>
                .
              </div>
            </>
          )}
        </div>
        <Switch
          checked={noMeetingDayEnabled}
          onChange={this.handleNoMeetingDayEnabledDayToggle}
          label={
            <div className="cw-flex cw-items-center cw-space-x-1">
              <div>Create a no-meeting day for your team on </div>
              <Select
                value={noMeetingDayOfWeek}
                onChange={this.onDayOfWeekChange}
                onClick={(e) => e.stopPropagation()}
                name="No Meeting Day of Week"
                disabled={!noMeetingDayEnabled}
                MenuProps={{
                  style: { zIndex: 10000 },
                }}
                inputProps={{
                  name: "No Meeting Day of Week",
                  id: "cw-no-meeting-day-of-week",
                }}
              >
                {daysOfWeek.map((d) => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </div>
          }
        />

        <FormControl disabled={!noMeetingDayEnabled} className="cw-mt-1 cw-ml-14">
          <FormControlLabel
            control={<Checkbox color="primary" checked={autoreplyEnabled} />}
            onChange={this.handleAutoreplyEnabledDayToggle}
            label={
              <div>
                <div
                  className={classNames("cw-body-lg", {
                    "cw-text-grey-darker": !noMeetingDayEnabled,
                  })}
                >
                  Autoreply to meeting organizers when they schedule over your team’s no-meeting day
                </div>
                <div
                  className={classNames("cw-body-base", {
                    "cw-text-slate-standard": noMeetingDayEnabled,
                    "cw-text-grey-darker": !noMeetingDayEnabled,
                  })}
                >
                  Clockwise will send an email informing them of your no-meeting day and suggesting
                  they reschedule
                </div>
              </div>
            }
            labelPlacement="end"
          />
        </FormControl>
      </React.Fragment>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////

//////////////////
// REDUX
//////////////////

const TeamSettingsAdminNoMeetingDayRedux = connect(null, {
  snackBarOpen,
})(TeamSettingsAdminNoMeetingDayCmpt);

//////////////////
// RELAY
//////////////////
export const TeamSettingsAdminNoMeetingDay = createRefetchContainer<IContainer>(
  TeamSettingsAdminNoMeetingDayRedux,
  teamSettingsAdminNoMeetingDayFragments,
  refetchQuery,
);
